import { Suspense, lazy } from 'react';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Skeleton } from '@/components/ui/skeleton';

import { useFetch } from 'hooks/useFetch';

import { type IPlotlyElement } from 'client-types/';

const Plot = lazy(() => import('react-plotly.js'));

interface Props {
  element: IPlotlyElement;
}

const _PlotlyElement = ({ element }: Props) => {
  const { data, error, isLoading } = useFetch(element.url || null);

  if (isLoading) {
    return <div>Loading...</div>;
  } else if (error) {
    return <div>An error occurred</div>;
  }

  let state;

  if (data) {
    state = data;
  } else {
    return null;
  }

  // Ensure Plotly layouts are responsive and don't force fixed width/height.
  const sanitizePlotlyLayout = (layout?: any) => {
    const { width: _w, height: _h, margin, ...rest } = layout || {};
    const defaultMargins = { l: 40, r: 28, t: 50, b: 40, pad: 0 };
    return { autosize: true, ...rest, margin: { ...defaultMargins, ...(margin || {}) } };
  };

  // Choose a sensible container height: use provided layout.height or default to 500px.
  const computedHeight = typeof state?.layout?.height === 'number' ? state.layout.height : 500;

  return (
    <div className="relative w-full min-w-0 overflow-hidden" style={{ height: computedHeight }}>
      <Suspense fallback={<Skeleton className="h-full rounded-md" />}>
        <Plot
          className={`${element.display}-plotly`}
          data={state.data}
          layout={sanitizePlotlyLayout(state.layout)}
          frames={state.frames}
          config={{ responsive: true, ...state.config }}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '1rem',
            overflow: 'hidden'
          }}
          useResizeHandler={true}
        />
      </Suspense>
    </div>
  );
};

const PlotlyElement = (props: Props) => {
  return (
    <ErrorBoundary prefix="Failed to load chart.">
      <_PlotlyElement {...props} />
    </ErrorBoundary>
  );
};

export { PlotlyElement };
